import { CopyClientVerificationResult, LeaseAnalysisSummary } from '../interfaces/copy-client-verification.interface'

export type CopyBannerAction = 'copy' | 'refresh' | 'request' | 'go-to-client'

export interface CopyBanner {
  title: string
  text: string
  action: CopyBannerAction
}

const day = (s: LeaseAnalysisSummary | null): string =>
  s?.verifiedAt ? new Date(s.verifiedAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Mexico_City' }) : ''
const money = (n: number): string => n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 })

/**
 * B-004 · El aviso de la pestaña Algoritmo cuando el análisis del arrendamiento vino vacío o no existe, y qué acción
 * ofrece: copiar el del cliente; si el cliente no tiene uno útil, correrlo aquí («Actualizar datos», con su CIEC
 * guardada) o pedírsela («Solicitar CIEC»). Un arrendamiento congelado no corre análisis: se le manda a la ficha del
 * cliente. Con un análisis con datos no hay aviso: ese queda como está.
 */
export function copyBannerOf(preview: CopyClientVerificationResult | null | undefined): CopyBanner | null {
  if (!preview) return null
  if (preview.canCopy) {
    const vacio = !!preview.before.verifiedAt
    return {
      title: vacio ? 'El análisis de este arrendamiento vino vacío' : 'Este arrendamiento no tiene análisis fiscal',
      text: `${vacio ? `Syntage no regresó datos el ${day(preview.before)}. ` : ''}El cliente tiene un análisis del ${day(preview.after)} con oferta máxima de ${money(preview.after?.maxOffer ?? 0)}.`,
      action: 'copy',
    }
  }
  if (preview.code !== 'NO_CLIENT_ANALYSIS' && preview.code !== 'CLIENT_ANALYSIS_EMPTY') return null
  if (!preview.before.empty) return null
  const title = preview.code === 'NO_CLIENT_ANALYSIS' ? 'El cliente todavía no tiene un análisis fiscal' : 'El análisis más reciente del cliente también vino vacío'
  if (preview.frozen) return { title, text: 'Hay que correrlo desde la ficha del cliente; después podrás copiarlo aquí.', action: 'go-to-client' }
  return preview.clientCiec?.stored
    ? { title, text: 'El cliente tiene su CIEC guardada: actualiza los datos para correr el análisis.', action: 'refresh' }
    : { title, text: 'El cliente no tiene CIEC guardada: solicítasela para poder correr el análisis.', action: 'request' }
}
